import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  FormControl,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { InputBase, InputLabel } from "components/common/Input";
import { useEffect, useMemo, useState } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import useLocations from "hooks/locations/useLocations";
import { formatLocations } from "utils/helpers";
import { useFormikContext } from "formik";
import useCurrentUser from "hooks/users/useCurrentUser";
import { AssetRetrievalBulkOrderDetails } from "types/assetRetrieval";
import ReturnAddressForm from "./ReturnAddressForm";
import { AssetRetrieval } from "hooks/assetRetrievals/useAssetRetrieval";
import useFeatureFlag from "hooks/useFeatureFlag";

interface ReturnDetailsProps {
  setCustomReturnLabel: (value: boolean) => void;
  isCanadaEmployeeAddress: boolean;
}
interface AutocompleteOption {
  label: string;
  id: string | number | undefined;
}

export default function ReturnAddressRadioGroup({ setCustomReturnLabel, isCanadaEmployeeAddress }: ReturnDetailsProps) {
  const [currentLocation, setCurrentLocation] = useState<AutocompleteOption>({ id: "", label: "" });
  const [locationOptions, setLocationOptions] = useState<AutocompleteOption[]>([{ id: "", label: "" }]);
  const [formLocation, setFormLocation] = useState<any>({});
  const { enabled: canadaToAnyUsaAddressEnabled } = useFeatureFlag("canada_to_any_us_address_enabled");
  const specificCountryCode = useMemo(() => {
    if (!isCanadaEmployeeAddress) {
      return "US";
    }

    if (canadaToAnyUsaAddressEnabled) {
      // Allowing all addresses to
      return undefined;
    } else {
      return "CA";
    }
  }, [canadaToAnyUsaAddressEnabled, isCanadaEmployeeAddress]);
  const { locations, refetch: refetchLocations } = useLocations(specificCountryCode);
  const { currentUser } = useCurrentUser();
  const { getFieldProps, values, errors, touched, setValues, setTouched, setFieldValue } = useFormikContext<
    AssetRetrievalBulkOrderDetails | AssetRetrieval
  >();
  const { enabled: canadaToUsaEnabled } = useFeatureFlag("canada_to_usa_enabled");

  useMemo(() => {
    const options = formatLocations(locations, "id");
    setLocationOptions(options);
  }, [locations]);

  const [selectedReturnMethod, setSelectedReturnMethod] = useState("location");

  useMemo(() => {
    const newLocation = locationOptions?.find(({ id }: any) => id === values.locationId);
    setCurrentLocation(newLocation || { id: "", label: "" });
  }, [values.locationId, locations]);

  useEffect(() => {
    const location = locations.filter((loc) => loc.id === currentLocation.id)[0];
    setFormLocation(location);

    if (location && location.locationContacts) {
      const lastContact =
        location.locationContacts.length > 0
          ? location.locationContacts[location.locationContacts.length - 1]
          : undefined;
      if (!lastContact?.firstName) {
        setFieldValue("returnRecipientFirstName", currentUser.firstName);
        setFieldValue("returnRecipientLastName", currentUser.lastName);
      } else {
        setFieldValue("returnRecipientFirstName", lastContact.firstName);
        setFieldValue("returnRecipientLastName", lastContact.lastName);
      }
    }
  }, [values.locationId, currentLocation, formLocation]);

  useEffect(() => {
    if (values.virtualPickup || values.locationId) {
      setValues((prevValues) => ({
        ...prevValues,
        returnRecipientName: "",
        returnRecipientFirstName: "",
        returnRecipientLastName: "",
        returnRecipientCompany: "",
        returnRecipientAddress: "",
        returnRecipientAddressTwo: "",
        returnRecipientAddressCity: "",
        returnRecipientAddressState: "",
        returnRecipientAddressZipCode: "",
        returnRecipientAddressCountry: "",
      }));

      setTouched({});
    }
  }, [values.virtualPickup, values.locationId]);

  const returnMethodValues = useMemo(
    () => ({
      location: {
        virtualPickup: false,
        iscCustomAddress: false,
      },
      virtualPickup: {
        returnRecipientFirstName: "",
        returnRecipientLastName: "",
        locationId: "",
        virtualPickup: true,
        iscCustomAddress: false,
      },
      customReturnLocation: {
        returnRecipientFirstName: "",
        returnRecipientLastName: "",
        virtualPickup: false,
        locationId: "",
        isCustomAddress: true,
      },
    }),
    [currentUser]
  );

  const onChangeReturnMethod = (value: string) => {
    const newValues = returnMethodValues[value as keyof typeof returnMethodValues];
    if (newValues) {
      setValues({
        ...values,
        ...newValues,
      });
    }

    if (value === "customReturnLocation") {
      setCustomReturnLabel(true);
      setCurrentLocation({ id: "", label: "" });
    } else {
      setCustomReturnLabel(false);
    }
  };

  useEffect(() => {
    setSelectedReturnMethod("location");
    onChangeReturnMethod("location");
    setCurrentLocation({ id: "", label: "" });
    refetchLocations();
  }, [isCanadaEmployeeAddress]);

  return (
    <>
      <FormControl>
        <RadioGroup
          aria-labelledby="demo-radio-buttons-group-label"
          value={selectedReturnMethod}
          name="radio-buttons-group"
          sx={{ gap: 2, mt: 2 }}
          onChange={(event) => {
            setSelectedReturnMethod(event.target.value);
            onChangeReturnMethod(event.target.value);
          }}
        >
          <FormControlLabel
            value="location"
            control={<Radio sx={{ pt: 0 }} />}
            label={
              <>
                <Typography fontWeight={600} fontSize="16px">
                  Office Location
                </Typography>
                <Typography variant="body2">Send your retrieved laptop to one of your saved locations.</Typography>
              </>
            }
          />
          {selectedReturnMethod === "location" && (
            <Box sx={{ pl: 4 }}>
              <Autocomplete
                sx={{ width: "546px" }}
                forcePopupIcon
                options={locationOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option: any, value: any) => {
                  return option.id === value?.id;
                }}
                onChange={(_event, newValue: any) => {
                  setCustomReturnLabel(false);
                  setValues({ ...values, locationId: newValue.id, isCustomAddress: false });
                }}
                renderInput={(params) => {
                  return (
                    <FormControl variant="standard" sx={{ width: "100%" }}>
                      <InputBase
                        ref={params.InputProps.ref}
                        inputProps={params.inputProps}
                        placeholder="Select Address"
                        error={touched.locationId && !!errors.locationId}
                        helperText={touched.locationId && errors.locationId}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end" sx={{ mr: 1 }}>
                              <ArrowDropDownIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </FormControl>
                  );
                }}
                value={locationOptions?.find(({ id }: any) => id === currentLocation.id)}
              />
              {formLocation && (
                <Box mt={1}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "2fr 2fr", gap: 2, mb: 1 }}>
                    <FormControl variant="standard">
                      <InputLabel shrink htmlFor="city">
                        First Name
                      </InputLabel>
                      <InputBase
                        type="text"
                        {...getFieldProps("returnRecipientFirstName")}
                        error={touched.returnRecipientFirstName && !!errors.returnRecipientFirstName}
                        helperText={touched.returnRecipientFirstName && errors.returnRecipientFirstName}
                      />
                    </FormControl>

                    <FormControl variant="standard">
                      <InputLabel shrink htmlFor="state">
                        Last Name
                      </InputLabel>
                      <InputBase
                        type="text"
                        {...getFieldProps("returnRecipientLastName")}
                        error={touched.returnRecipientLastName && !!errors.returnRecipientLastName}
                        helperText={touched.returnRecipientLastName && errors.returnRecipientLastName}
                      />
                    </FormControl>
                  </Box>
                  <Alert severity="info">
                    <AlertTitle sx={{ fontWeight: 700 }}>Please check this address and contact are correct.</AlertTitle>

                    <Box>
                      <Typography>
                        {values.returnRecipientFirstName} {values.returnRecipientLastName}
                      </Typography>
                      <Typography>{formLocation.address}</Typography>
                      <Typography>{formLocation.address2}</Typography>
                      <Typography>{formLocation.city}</Typography>
                      <Typography>{formLocation.zipcode}</Typography>
                      <Typography>{formLocation.country}</Typography>
                    </Box>
                  </Alert>
                </Box>
              )}
            </Box>
          )}

          <FormControlLabel
            value="virtualPickup"
            control={<Radio sx={{ pt: 0 }} />}
            sx={{ display: isCanadaEmployeeAddress && !canadaToUsaEnabled ? "none" : "" }}
            label={
              <Box display="flex" flexDirection="column">
                <Typography fontWeight={600} fontSize="16px">
                  Direct to Revivn
                </Typography>
                <Typography variant="body2">
                  Send your end of life asset directly to Revivn and we'll waive all shipping and processing fees.
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            value="customReturnLocation"
            control={<Radio />}
            label={
              <Typography fontWeight={600} fontSize="16px">
                Custom Return Address
              </Typography>
            }
          />
        </RadioGroup>
      </FormControl>
      {selectedReturnMethod === "customReturnLocation" && (
        <ReturnAddressForm isCanadaEmployeeAddress={isCanadaEmployeeAddress} />
      )}
    </>
  );
}
